import React, { useCallback, useState } from 'react';
import { LowLevelPaymentMethod } from 'interface/LowLevelPaymentMethod';
import styles from 'styles/components/CardPaymentMethod.module.sass';
import BrandIcon from 'components/common/BrandIcon';
import SecondaryButton from 'components/common/SecondaryButton';
import PrimaryButton from 'components/common/PrimaryButton';
import { useToast } from 'provider/ToastProvider';
import { setDefaultPaymentMethod, detachPaymentMethod } from 'api/PaymentApiService';

const tag = 'CARD_PAYMENT_METHOD';

interface CardPaymentMethodProps {
    paymentMethod: LowLevelPaymentMethod;
    knownPaymentMethodsCount?: number;
    setChangeDetected?: (changeDetected: boolean) => void;
}

/**
 * CardPaymentMethod component - Displays a payment method in a card format.
 * @component
 * 
 * @param {CardPaymentMethodProps} props The component props.
 * @param {LowLevelPaymentMethod} props.paymentMethod The payment method to display.
 * @example
 * <CardPaymentMethod paymentMethod={{ brand: 'Visa', last4: '1234', exp: '12/23', type: 'credit' }} />
 * 
 * @returns {JSX.Element} CardPaymentMethod component
 */
const CardPaymentMethod: React.FC<CardPaymentMethodProps> = ({ paymentMethod, knownPaymentMethodsCount, setChangeDetected }) => {
    const [isBusy, setIsBusy] = useState<boolean>(false);
    const { addToast } = useToast();

    const showUpdatingToast = () => {
        addToast({
            type: 'info',
            message: 'Updating payment methods...',
        });
    }

    const canRemove = useCallback(() => {
        if (knownPaymentMethodsCount && knownPaymentMethodsCount <= 1) {
            addToast({
                type: 'error',
                message: 'You must have at least one payment method.',
            });
            return false;
        }

        if (paymentMethod.default) {
            addToast({
                type: 'error',
                message: 'Set another payment method as default before removing this one.',
            });
            return false;
        }
        return true;
    }, [knownPaymentMethodsCount, paymentMethod.default, addToast]);

    const performRemove = useCallback(async () => {
        if (!canRemove() || isBusy) return;

        setIsBusy(true);
        showUpdatingToast();

        try {
            const ok = await detachPaymentMethod({ tag, paymentMethodId: paymentMethod.id });
            if (!ok) {
                addToast({
                    type: 'error',
                    message: 'Failed to remove payment method.',
                });
                return;
            }

            addToast({
                type: 'success',
                message: 'Payment method removed successfully.',
            });
            setChangeDetected?.(true)
        } catch {
            addToast({
                type: 'error',
                message: 'Failed to remove payment method.',
            });
        } finally {
            setIsBusy(false);
        }
    }, [canRemove, isBusy, paymentMethod.id, addToast, setChangeDetected]);

    const performSetDefault = useCallback(async () => {
        if (paymentMethod.default || isBusy) {
            addToast({
                type: 'info',
                message: 'This payment method is already the default.',
            });
            return;
        }

        setIsBusy(true);
        showUpdatingToast();

        try {
            const ok = await setDefaultPaymentMethod({ tag, paymentMethodId: paymentMethod.id });
            if (!ok) {
                addToast({
                    type: 'error',
                    message: 'Failed to set payment method as default.',
                });
                return;
            }
            addToast({
                type: 'success',
                message: 'Payment method set as default successfully.',
            });
            setChangeDetected?.(true)
        } catch {
            addToast({
                type: 'error',
                message: 'Failed to set payment method as default.',
            });
        } finally {
            setIsBusy(false);
        }
    }, [paymentMethod.default, isBusy, paymentMethod.id, addToast, setChangeDetected]);

    return (
        <div className={styles['card']}>
            <div className={styles['content']}>
                <div className={styles['header-stripe']}></div>

                <div className={styles['header']}>
                    <BrandIcon brand={paymentMethod.brand} />
                    {paymentMethod.default && (
                        <div className={styles['default-pill']}>
                            Default
                        </div>
                    )}
                </div>

                <div className={styles['card-number']}>
                    {paymentMethod.cardNumber}
                </div>

                <div className={styles['details']}>
                    <div className={styles['detail-item']}>
                        <span className={styles['label']}>Exp:</span>
                        <span className={styles['value']}>{paymentMethod.expirationDate}</span>
                    </div>
                    <div className={styles['detail-item']}>
                        <span className={styles['label']}>Type:</span>
                        <span className={styles['value']}>{paymentMethod.type}</span>
                    </div>
                    <div className={styles['detail-item']}>
                        <span className={styles['label']}>Brand:</span>
                        <span className={styles['value']}>{paymentMethod.brand.toUpperCase()}</span>
                    </div>
                </div>
            </div>
            <div className={styles['action-buttons']}>
                <PrimaryButton
                    width='100%'
                    text='Make Default'
                    primaryColor='#FF5522'
                    textColor='#FFFFFF'
                    hoverColor='#FFFFFF'
                    hoverTextColor='#FF5522'
                    icon={paymentMethod.default ? 'material-symbols:star-rounded' : 'material-symbols:star-outline-rounded'}
                    doSomething={() => { void performSetDefault() }}
                />

                <SecondaryButton
                    width='100%'
                    text='Delete'
                    primaryColor='transparent'
                    textColor='#136A97'
                    hoverColor='#136A97'
                    hoverTextColor='#FFFFFF'
                    icon={'material-symbols:delete-outline-rounded'}
                    doSomething={() => { void performRemove() }}
                />

            </div>

        </div>
    );
}

export default CardPaymentMethod;